import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TableCard from "../../../components/ui-components/TableCard";
import FlexTable from "../../../components/ui-components/FlexTable";
import EmptyState from "../../../components/ui-components/EmptyState";
import ThresholdBar from "./ThresholdBar";
import { normalizeStatus, sortScopeRows, isAttention, riskRowModel, outlookModel } from "../overviewDerive";
import { StatusBadge as ControlStatusBadge } from "../../cost-control/BudgetExceptionsTable";
import OwnerCell from "../../cost-control/OwnerCell";
import { humanEUR } from "../../cost-control/money";

const DEFAULT_SCOPE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "org", label: "Organisation" },
  { value: "financial", label: "Financial" },
  { value: "technical", label: "Technical" },
  { value: "usecase", label: "Use-case" },
];

// Map enforcement status → Control's StatusBadge vocabulary (Over / At risk / On track / …).
function mapEnforcementToControlStatus(status) {
  const n = normalizeStatus(status);
  if (n === "breaching") return "over";
  if (n === "at_risk") return "at_risk";
  if (n === "ok") return "on_track";
  return "not_measurable";
}

// Bar tone. A healthy budget gets a NEUTRAL fill, not a green one: colouring every resting bar
// green makes the calmest rows the loudest thing on screen and leaves nothing in reserve for the
// rows that need a reader. Colour is spent only on states that warrant intervention.
function toneFor(status) {
  const n = normalizeStatus(status);
  if (n === "breaching") return "danger";
  if (n === "at_risk") return "warning";
  return "neutral";
}

const OUTLOOK_TEXT = { danger: "text-danger", warning: "text-warning", neutral: "text-text-2" };

// StatusCell — an on-track row states its health with a small dot and plain text; only at-risk
// and over rows get the filled badge. Same reasoning as the bar tone: abnormal states should
// attract the eye, healthy ones should not.
function StatusCell({ status }) {
  const control = mapEnforcementToControlStatus(status);
  if (control === "on_track") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-text-2">
        <span className="inline-block rounded-full bg-success" style={{ width: 7, height: 7 }} />
        On track
      </span>
    );
  }
  return <ControlStatusBadge status={control} />;
}
StatusCell.propTypes = { status: PropTypes.string };

// CapUsageCell answers three different questions in one column, each with its own line:
// "where are we?" (the percentage, dominant), "how does this row compare to the others?" (the
// bar), and "what does that percentage mean in money?" (actual of cap). The bar is sized to be
// read, not glanced at as a sparkline.
function CapUsageCell({ model, status }) {
  // width is inline, not `w-full`: this repo overrides Tailwind's width scale, so w-full emits
  // nothing and the cell — a flex child — collapses to the width of its longest text line,
  // cropping the bar to about a third of the column.
  return (
    <div className="min-w-0" style={{ width: "100%", paddingRight: 28 }}>
      <div className="text-sm font-semibold tabular-nums text-text">{model.nowPct}% used</div>
      <div className="mt-1.5">
        <ThresholdBar
          nowPct={model.nowPct}
          marks={model.marks}
          tone={toneFor(status)}
          projectedPct={model.projectedPct}
          height={12}
        />
      </div>
      <div className="mt-1.5 tabular-nums text-[11px] text-text-3">
        {humanEUR(model.actual)} of {humanEUR(model.budget)}
      </div>
    </div>
  );
}
CapUsageCell.propTypes = { model: PropTypes.object, status: PropTypes.string };

// OutlookCell states the expected end-of-period outcome rather than the past. Every row lands on
// a real financial state, so there is no bare "—" pretending to be data.
function OutlookCell({ outlook }) {
  const hasForecast = outlook.projectedPct != null && outlook.state !== "no_spend" && outlook.state !== "no_cap";
  return (
    <div className="min-w-0">
      {hasForecast ? (
        <div className="text-sm tabular-nums text-text">Forecast {outlook.projectedPct}%</div>
      ) : null}
      <div className={`${hasForecast ? "mt-0.5 text-[11px]" : "text-xs"} font-medium ${OUTLOOK_TEXT[outlook.tone]}`}>
        {outlook.label}
      </div>
      {outlook.overage ? (
        <div className="mt-0.5 tabular-nums text-[11px] text-text-3">+{humanEUR(outlook.overage)} over cap</div>
      ) : null}
    </div>
  );
}
OutlookCell.propTypes = { outlook: PropTypes.object };

// BudgetsTable — budgets through the governance lens. The columns run current consumption →
// expected outcome → control state: Cap usage (spend against the cap), Outlook (whether the
// period is likely to end inside it), Policy (governed vs ungoverned). A row click opens the
// enforcement drawer. Built on the shared TableCard + FlexTable.
export default function BudgetsTable({
  rows = [],
  policies = [],
  scope = "all",
  onScopeChange,
  scopeOptions = DEFAULT_SCOPE_OPTIONS,
  attentionOnly = false,
  onAttentionChange,
  pendingBudgetIds,
  onSelect,
  onAction,
}) {
  const pending = pendingBudgetIds || new Set();

  const labelByCode = useMemo(() => {
    const m = {};
    for (const opt of scopeOptions) if (opt.value !== "all") m[opt.value] = opt.label;
    return m;
  }, [scopeOptions]);

  const atScope = scope === "all" ? rows : rows.filter((r) => r.scope_level === scope);
  const attentionCount = atScope.filter(isAttention).length;
  const visible = sortScopeRows(attentionOnly ? atScope.filter(isAttention) : atScope);

  const data = useMemo(
    () =>
      visible.map((r) => {
        const model = riskRowModel(r, policies);
        // Sign-off is automatic: a budget shows "awaiting" only when an auto-created approval
        // request is actually pending for it.
        const signoff = pending.has(r.budget_id) ? "awaiting" : "none";
        return {
          ...r,
          _model: model,
          _outlook: outlookModel(r, model.marks),
          _scopeLabel: labelByCode[r.scope_level] || model.scopeLabel,
          _signoff: signoff,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible, policies, labelByCode]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "status",
        Header: () => <span>Status</span>,
        Cell: ({ value }) => <StatusCell status={value} />,
        width: 118,
        minWidth: 104,
        maxWidth: 140,
      },
      {
        id: "budget",
        Header: () => <span>Budget</span>,
        Cell: ({ row }) => {
          const m = row.original._model;
          return (
            <div className="min-w-0">
              <div className="truncate font-medium text-text">{row.original.name}</div>
              <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-text-3">
                <span className="rounded bg-side px-1.5 py-0.5 font-medium text-text-2">{row.original._scopeLabel}</span>
                <OwnerCell owners={row.original.owners} owner={m.ownerLabel} details={row.original.owner_details} />
                {/* Approval no longer earns a column of its own — it was empty for most rows. The
                    signal rides with the budget it blocks; the Review button carries the action. */}
                {row.original._signoff === "awaiting" ? (
                  <span className="rounded bg-warning-soft px-1.5 py-0.5 font-medium text-warning">Awaiting approval</span>
                ) : null}
              </div>
            </div>
          );
        },
        flex: 1,
        minWidth: 220,
      },
      {
        id: "cap",
        Header: () => <span>Cap usage</span>,
        Cell: ({ row }) => <CapUsageCell model={row.original._model} status={row.original.status} />,
        // The widest column on the row: the bar is the only element here that has to be *measured*
        // by eye rather than read, and it can only be measured if it is long enough to resolve a
        // few percent. The width freed by dropping the side rail goes here.
        width: 300,
        minWidth: 250,
        maxWidth: 360,
      },
      {
        id: "outlook",
        Header: () => <span>Outlook</span>,
        Cell: ({ row }) => <OutlookCell outlook={row.original._outlook} />,
        width: 150,
        minWidth: 130,
        maxWidth: 180,
      },
      {
        id: "policy",
        Header: () => <span>Policy</span>,
        Cell: ({ row }) =>
          row.original._model.ungoverned ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-warning">
              <FontAwesomeIcon icon={icon({ name: "triangle-exclamation", style: "solid" })} className="text-[10px]" />
              None
            </span>
          ) : (
            // Named in full, wrapping if it must: a truncated policy name ("General spend guardra…")
            // identifies nothing, and why a cap matters is exactly what this column is for.
            <span className="text-xs leading-snug text-text-2">
              {row.original.policy_name || row.original._model.governed?.name}
            </span>
          ),
        width: 170,
        minWidth: 140,
        maxWidth: 210,
      },
      {
        id: "action",
        align: "right",
        Header: () => null,
        Cell: ({ row }) => {
          // Ungoverned → attach a policy (the root-cause fix). Awaiting a sign-off → review the
          // auto-created request. Otherwise no action (the row opens the drawer on click).
          let kind = null;
          let label = null;
          if (row.original._model.ungoverned) {
            kind = "attach_policy";
            label = "Attach policy";
          } else if (row.original._signoff === "awaiting") {
            kind = "review";
            label = "Review";
          }
          if (!kind) return null;
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAction && onAction(kind, row.original);
              }}
              className="rounded-ctrl bg-primary px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-primary-hover"
            >
              {label}
            </button>
          );
        },
        width: 150,
        minWidth: 130,
        maxWidth: 170,
      },
    ],
    [onAction]
  );

  const subtitle =
    attentionCount > 0
      ? `${attentionCount} need${attentionCount === 1 ? "s" : ""} attention · ${atScope.length} total`
      : `${atScope.length} budget${atScope.length === 1 ? "" : "s"} · all on track`;

  const attentionControl = (
    <div className="inline-flex items-center gap-1.5" aria-label="Filter by attention">
      {[
        { value: false, label: "All" },
        { value: true, label: "Attention" },
      ].map((opt) => {
        const active = attentionOnly === opt.value;
        return (
          <button
            key={opt.label}
            type="button"
            onClick={() => onAttentionChange(opt.value)}
            className={`box-border rounded-full border border-solid px-2.5 py-1 text-xs ${
              active ? "border-primary bg-primary-soft font-semibold text-primary" : "border-card-bd bg-card text-text-2"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <TableCard
      title="Budgets"
      subtitle={subtitle}
      filterValue={scope}
      onFilterChange={onScopeChange}
      filterOptions={scopeOptions}
      filterAriaLabel="Budget perspective"
      controls={attentionControl}
      controlsPlacement="second-row"
    >
      {data.length === 0 ? (
        <EmptyState
          icon={icon({ name: "circle-check", style: "light" })}
          title={attentionOnly ? "Nothing needs attention" : "No budgets"}
          message={
            attentionOnly
              ? "Every budget at this scope is on track."
              : "Budgets will appear here once they're set up."
          }
          action={
            attentionOnly
              ? { label: "Show all budgets", onClick: () => onAttentionChange(false), variant: "secondary" }
              : undefined
          }
        />
      ) : (
        <FlexTable
          columns={columns}
          data={data}
          getRowId={(r) => String(r.budget_id)}
          onRowClick={(id, r) => onSelect && onSelect(r)}
          rowAriaLabel={(r) => `Open ${r.name}`}
        />
      )}
    </TableCard>
  );
}

BudgetsTable.propTypes = {
  rows: PropTypes.array,
  policies: PropTypes.array,
  scope: PropTypes.string,
  onScopeChange: PropTypes.func,
  scopeOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
  attentionOnly: PropTypes.bool,
  onAttentionChange: PropTypes.func,
  pendingBudgetIds: PropTypes.instanceOf(Set),
  onSelect: PropTypes.func,
  onAction: PropTypes.func,
};
